import React, { useEffect, useState } from "react";
import { playlist as pApi } from "../lib/api";
import { toast } from "sonner";
import { Search, Plus, Trash2, Import, Share2, Copy, Check, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";

export default function PlaylistTab({ playlist, refresh, onLoadDeck }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [copied, setCopied] = useState(false);

  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const data = await pApi.search(q);
      setResults(data.results || []);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const addFromResult = async (item) => {
    try {
      await pApi.add(item.video_id);
      toast.success(`Added: ${item.title}`);
      refresh();
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  const addFromUrl = async () => {
    if (!q.trim()) return;
    try {
      const song = await pApi.add(q);
      toast.success(`Added: ${song.title}`);
      setQ("");
      setResults([]);
      refresh();
    } catch (e) {
      toast.error("Invalid YouTube URL or ID");
    }
  };

  const remove = async (uid) => {
    try {
      await pApi.remove(uid);
      refresh();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const move = async (index, delta) => {
    const songs = playlist?.songs || [];
    const target = index + delta;
    if (target < 0 || target >= songs.length) return;
    const next = songs.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    try {
      await pApi.reorder(next.map((s) => s.song_uid));
      refresh();
    } catch (e) {
      toast.error("Failed to reorder");
    }
  };

  const doImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const res = await pApi.import(importUrl);
      toast.success(`Imported ${res.imported} tracks`);
      setImportUrl("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const shareUrl = playlist?.share_token
    ? `${window.location.origin}/party/${playlist.share_token}`
    : "";

  const copyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const songs = playlist?.songs || [];

  return (
    <div className="space-y-4">
      {/* Search / Add */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-zinc-500" />
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">SEARCH YOUTUBE OR PASTE URL</span>
        </div>
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (q.includes("youtu")) addFromUrl();
                else doSearch();
              }
            }}
            placeholder="Search 'Diplo remix' or paste https://youtu.be/..."
            className="bg-zinc-900 border-zinc-800 text-white"
            data-testid="search-input"
          />
          <Button
            onClick={doSearch}
            disabled={searching}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="search-btn"
          >
            {searching ? "..." : "Search"}
          </Button>
          <Button
            onClick={addFromUrl}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-900"
            data-testid="add-url-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {results.length > 0 && (
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
            {results.map((r) => (
              <div
                key={r.video_id}
                className="flex items-center gap-3 p-2 rounded-lg bg-zinc-900/60 hover:bg-zinc-900"
              >
                <div className="relative shrink-0">
                  <img src={r.thumbnail} alt="" className="w-16 h-12 object-cover rounded" />
                  {r.duration_seconds ? (
                    <span
                      className="absolute bottom-0.5 right-0.5 px-1 py-px rounded font-mono text-[9px] leading-none text-white"
                      style={{ background: "rgba(0,0,0,0.75)" }}
                      data-testid={`duration-${r.video_id}`}
                    >
                      {fmtDuration(r.duration_seconds)}
                    </span>
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{r.channel}</div>
                </div>
                <Button
                  size="sm"
                  onClick={() => addFromResult(r)}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white"
                  data-testid={`add-result-${r.video_id}`}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import + Share */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Import className="w-4 h-4 text-cyan-400" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">IMPORT PLAYLIST</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://youtube.com/playlist?list=..."
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="import-input"
            />
            <Button
              onClick={doImport}
              disabled={importing}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="import-btn"
            >
              {importing ? "..." : "Import"}
            </Button>
          </div>
          <div className="mt-2 text-[10px] text-zinc-500 leading-relaxed">
            Signed in to YouTube?{" "}
            <a
              href="https://www.youtube.com/feed/library"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-zinc-400 hover:text-white"
              data-testid="yt-library-link"
            >
              Open your library →
            </a>{" "}
            then copy any playlist URL back here.
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 p-4 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(217,70,239,0.12), rgba(6,182,212,0.10)), #0a0a0b",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="w-4 h-4 text-fuchsia-400" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-300">INVITE FRIENDS</span>
          </div>
          <div className="text-xs text-zinc-400 mb-2">Share this link — friends can add songs without login.</div>
          <div className="flex gap-2">
            <Input
              readOnly
              value={shareUrl}
              className="bg-zinc-900 border-zinc-800 text-white text-xs"
              data-testid="share-link"
            />
            <Button
              onClick={copyShare}
              className="bg-white text-black hover:bg-zinc-200"
              data-testid="copy-share-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Track list */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <span className="font-heading font-bold">Party Playlist</span>
          <span className="font-mono text-[10px] text-zinc-500">{songs.length} TRACKS</span>
        </div>
        {songs.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            No tracks yet. Search above or paste a YouTube link to get started.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {songs.map((s, i) => (
              <div key={s.song_uid} className="flex items-center gap-3 p-3 hover:bg-zinc-900/40">
                <span className="font-mono text-xs text-zinc-600 w-6 text-right">{i + 1}</span>
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="p-0.5 rounded text-zinc-500 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-up-${s.song_uid}`}
                    title="Move up"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === songs.length - 1}
                    className="p-0.5 rounded text-zinc-500 hover:text-white hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-down-${s.song_uid}`}
                    title="Move down"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
                <img src={s.thumbnail} alt="" className="w-14 h-10 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{s.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {s.channel} · {s.bpm} BPM · added by {s.added_by}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => onLoadDeck("A", s)}
                    className="px-2.5 py-1.5 rounded-md text-[10px] font-mono font-bold tracking-widest border"
                    style={{ color: "#d946ef", borderColor: "#d946ef55", background: "rgba(217,70,239,0.08)" }}
                    data-testid={`load-a-${s.song_uid}`}
                  >
                    LOAD A
                  </button>
                  <button
                    onClick={() => onLoadDeck("B", s)}
                    className="px-2.5 py-1.5 rounded-md text-[10px] font-mono font-bold tracking-widest border"
                    style={{ color: "#06b6d4", borderColor: "#06b6d455", background: "rgba(6,182,212,0.08)" }}
                    data-testid={`load-b-${s.song_uid}`}
                  >
                    LOAD B
                  </button>
                  <button
                    onClick={() => remove(s.song_uid)}
                    className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`remove-${s.song_uid}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
